import { EnumSelect } from "@/components/shared/enum-select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOCATION_OPTIONS } from "@/mocks/mass-update-reference";
import { WORK_ORDER_STATUS_OPTIONS, type WorkOrderSetValues } from "@/mocks/search-work-orders";

const FIRMED_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

interface SetValuesPanelProps {
  values: WorkOrderSetValues;
  onChange: (values: WorkOrderSetValues) => void;
  selectedCount: number;
  onClear: () => void;
}

export function SetValuesPanel({ values, onChange, selectedCount, onClear }: SetValuesPanelProps) {
  function setField<K extends keyof WorkOrderSetValues>(key: K, value: string) {
    onChange({ ...values, [key]: value });
  }

  return (
    <Card>
      <CardHeader className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle className="text-base">Set values</CardTitle>
          <p className="text-sm text-muted-foreground">
            Leave a field blank to leave it unchanged.{" "}
            {selectedCount > 0
              ? `${selectedCount} work order${selectedCount === 1 ? "" : "s"} currently selected.`
              : "Select work orders above first."}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onClear}>
          Clear
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Status</span>
            <EnumSelect
              value={values.status}
              onValueChange={(value) => setField("status", value)}
              options={WORK_ORDER_STATUS_OPTIONS}
              allOption={{ label: "Don't change" }}
              aria-label="Set status"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Location</span>
            <EnumSelect
              value={values.locationCode}
              onValueChange={(value) => setField("locationCode", value)}
              options={LOCATION_OPTIONS}
              allOption={{ label: "Don't change" }}
              aria-label="Set location"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Firmed</span>
            <EnumSelect
              value={values.firmed}
              onValueChange={(value) => setField("firmed", value)}
              options={FIRMED_OPTIONS}
              allOption={{ label: "Don't change" }}
              aria-label="Set firmed"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="set-start-date">Start date</Label>
            <Input
              id="set-start-date"
              type="date"
              value={values.startDate}
              onChange={(event) => setField("startDate", event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="set-end-date">End date</Label>
            <Input
              id="set-end-date"
              type="date"
              value={values.endDate}
              onChange={(event) => setField("endDate", event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="set-memo">Memo</Label>
            <Input
              id="set-memo"
              placeholder="Leave blank to leave unchanged"
              value={values.memo}
              onChange={(event) => setField("memo", event.target.value)}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
